/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: ThemeTab.tsx
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Theme tab component for Appearance settings.

 🔁 Last Updated: 2025-01-XX by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import React from 'react';
import { Monitor, Sun, Square } from 'lucide-react';
import { useI18n } from '../../../hooks/useI18n';
import { FormBox } from '../../../components/ui/FormBox';
import { ThemePreview, LightColorSchemePreview, DarkColorSchemePreview } from '../../../components/ui/ThemePreview';
import type { AppearanceForm } from '../types';
import type { UseFormRegister, UseFormSetValue } from 'react-hook-form';

interface ThemeTabProps {
  watchedValues: AppearanceForm;
  register: UseFormRegister<AppearanceForm>;
}

export function ThemeTab({ watchedValues, register }: ThemeTabProps) {
  const { t } = useI18n();

  const darkModeVariants = [
    { value: 'mint', scheme: 'mint' as const },
    { value: 'navy', scheme: 'navy' as const },
    { value: 'mirage', scheme: 'mirage' as const },
    { value: 'cinder', scheme: 'cinder' as const },
    { value: 'black', scheme: 'black' as const }
  ];

  const lightModeVariants = [
    { value: 'slate', scheme: 'slate' as const },
    { value: 'gray', scheme: 'gray' as const },
    { value: 'neutral', scheme: 'neutral' as const },
    { value: 'white', scheme: 'white' as const },
    { value: 'very-light-green', scheme: 'very-light-green' as const },
    { value: 'very-light-purple', scheme: 'very-light-purple' as const },
    { value: 'light-green', scheme: 'light-green' as const }
  ];

  return (
    <div className="space-y-3">
      {/* Theme Mode */}
      <FormBox title={t('appearance.themeMode.title')} icon={<Monitor />}>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { value: 'light', label: t('appearance.themeMode.light.label') },
            { value: 'dark', label: t('appearance.themeMode.dark.label') },
            { value: 'system', label: t('appearance.themeMode.system.label') }
          ].map((mode) => (
            <label key={mode.value} className="relative">
              <input
                {...register('mode')}
                type="radio"
                value={mode.value}
                className="sr-only"
              />
              <div className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${watchedValues.mode === mode.value
                  ? 'border-primary bg-primary/10'
                  : 'border-border hover:border-primary/50'
                }`}>
                <ThemePreview type={mode.value as 'system' | 'light' | 'dark'} className="mb-3" />
                <div className="text-center">
                  <div className="font-medium">{mode.label}</div>
                </div>
              </div>
            </label>
          ))}
        </div>
      </FormBox>

      {/* Dark Mode Variant */}
      {watchedValues.mode === 'dark' && (
        <FormBox title={t('appearance.darkModeVariant.title')} icon={<Square />}>
          <div className="grid grid-cols-5 gap-4">
            {darkModeVariants.map((variant) => (
              <label key={variant.value} className="relative">
                <input
                  {...register('darkModeVariant')}
                  type="radio"
                  value={variant.value}
                  className="sr-only"
                />
                <div className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${watchedValues.darkModeVariant === variant.value
                    ? 'border-primary bg-primary/10'
                    : 'border-border hover:border-primary/50'
                  }`}>
                  <DarkColorSchemePreview scheme={variant.scheme} className="mb-3" />
                  <div className="text-center">
                    <div className="font-medium">{t(`appearance.darkModeVariant.${variant.value}.label`)}</div>
                  </div>
                </div>
              </label>
            ))}
          </div>
        </FormBox>
      )}

      {/* Light Mode Variant */}
      {watchedValues.mode === 'light' && (
        <FormBox title={t('appearance.lightModeVariant.title')} icon={<Sun />}>
          <div className="grid grid-cols-7 gap-3">
            {lightModeVariants.map((variant) => (
              <label key={variant.value} className="relative">
                <input
                  {...register('lightModeVariant')}
                  type="radio"
                  value={variant.value}
                  className="sr-only"
                />
                <div className={`p-2 border-2 rounded-lg cursor-pointer transition-all ${watchedValues.lightModeVariant === variant.value
                    ? 'border-primary bg-primary/10'
                    : 'border-border hover:border-primary/50'
                  }`}>
                  <LightColorSchemePreview scheme={variant.scheme} className="mb-2" />
                  <div className="text-center">
                    <div className="text-xs font-medium">{t(`appearance.lightModeVariant.${variant.value}.label`)}</div>
                  </div>
                </div>
              </label>
            ))}
          </div>
        </FormBox>
      )}
    </div>
  );
}

